import type { Component } from 'vue'

import type { ReportViewType } from './chart.types'
import type { DataVisibleProps } from './dataVisible.types'
import type { FilterMatchMode, ReportFilter } from './filter.types'

import type { DatePresetId } from '@/shared/ui'

// The typed replacement for v3's `configs/**` layout objects (ORP-18652). One deliberate split
// runs through every level: v3's `name` was simultaneously machine identity (selection,
// persistence, backend filter values, icon lookup) AND display copy. v4 keeps `name` as the
// machine key — NEVER rendered raw — and carries copy in `labelKey`/`titleKey`/`descriptionKey`
// i18n keys under the `reports.*` namespace, which is what lets `no-raw-text` stay green
// across `configs/**`.

/**
 * Availability rule evaluated against the selected workspace's report schedules
 * (v3 `layoutStore.setReportLayout` + `workspaceStore.checkScheduleTabs`).
 */
export interface LayoutVisibleRule {
  reportId: number
  tabs: string[]
  /** Any listed column present in the matched schedule tab's `columns`. */
  checkCols?: string[]
  /** Any listed value present in the schedule's dropdownable dimension values. */
  checkDimension?: { field: string; value: string[] }
  /** Invert the rule: hide the carrier when it MATCHES (v3 `notValid`, ProductPerformance). */
  notValid?: boolean
}

/** v3 hardcoded `'Ad Level' | 'Creative Level'` copy-as-value; v4 keys the machine ids. */
export const SECTION_LEVELS = ['ad', 'creative'] as const
export type SectionLevelId = (typeof SECTION_LEVELS)[number]

/** The "Overall" entity-group sentinel (v3's `'Overall'` literal, now a machine id). */
export const SECTION_GROUP_OVERALL = 'overall'

export interface SectionTopTab {
  /** Machine identity — selection + persistence key. */
  name: string
  labelKey: string
}

export interface SectionDropdownOption {
  labelKey: string
  /** The filter payload this option applies; `undefined` means "no filter" (v3 first-option default). */
  value?: string | number | readonly (string | number)[]
  /** Optional availability gate: option shows only when the report's schedule carries these tabs. */
  reportId?: number
  tabs?: string[]
}

export interface SectionDropdown {
  filter: { field: string; matchMode: FilterMatchMode }
  labelKey: string
  options: SectionDropdownOption[]
}

export interface ReportSectionTabConfig {
  /** Machine identity — selection + persistence + icon/platform key, never displayed raw. */
  name: string
  /** `pending` marks a tab whose v3 renderer (n-gram / dashboard area) is not ported yet. */
  kind?: 'data' | 'pending'
  pendingNote?: string
  /** Display copy for top-tab mode; channel-rail tabs render platform logos off `name`. */
  labelKey?: string
  /** Schedule tab/partition names — the data-availability gate AND the request target set. */
  tabs?: string[]
  reportId?: number
  channel?: string
  checkCols?: string[]
  checkDimension?: { field: string; value: string[] }
  visibleRule?: LayoutVisibleRule
  /** Per-tab section heading/description override (v3 `selected.title`). */
  titleKey?: string
  descriptionKey?: string
  /** Enables the entity Group select fed by the workspace `custom_grouping` attribute. */
  entityType?: string
  dropdowns?: SectionDropdown[]
}

/** Everything a section's `build` resolves against for one rendered tab. */
export interface SectionBuildContext {
  tab: ReportSectionTabConfig
  /** The applied dropdown selections, already shaped as report filters. */
  dropdownFilters: ReportFilter[]
  /** Active creative level ('ad' unless the section enables the Level select). */
  level: SectionLevelId
  /** Active entity group name, or null for "Overall". Column-set application is
   *  blocked on ORP-18643's `partitionColumns`; the context seam ships first. */
  group: string | null
  topTab: SectionTopTab | null
}

/**
 * What a section `build` returns: the DataVisible configuration minus the host-supplied
 * fields (dates arrive from the page/section picker; state copy is section-generic).
 *
 * `dimensions`/`metrics`/`partitionName` are optional here although DataVisible requires
 * them: v3 derived the table field set from schedule partition metadata (`setupTabs`).
 * The section host bridges that derivation from `scheduleTabs` until ORP-18643's
 * `partitionColumns` pure service replaces it.
 */
export interface SectionDataVisibleConfig extends Omit<
  DataVisibleProps,
  | 'defaultPostData'
  | 'ariaLabel'
  | 'emptyLabel'
  | 'errorLabel'
  | 'retryLabel'
  | 'dimensions'
  | 'metrics'
  | 'partitionName'
> {
  /** Schedule tab names to target (v3 config `tabs`); the first match carries the partition. */
  scheduleTabs?: string[]
  dimensions?: string[]
  preferredDimensions?: string[]
  metrics?: string[]
  partitionName?: string
  /**
   * v3 `showKeyEvents: true`. Pre-selects every numeric key-event column the partition offers
   * (GA4/Hardal/MMP), on top of whatever `metrics` resolves to. It selects rather than restricts:
   * a key-events surface pairs it with `hideFields` to strip the standard metric set.
   */
  showKeyEvents?: boolean
  /**
   * v3 `hideCols`. Removes fields from the PRE-SELECTED dimension/metric sets only — they stay in
   * the offer universe, so the customize panel can bring them back (v3 `setupTabs:117-118`
   * deleted from `state`, never from `allCols`).
   */
  hideFields?: string[]
}

/** v3 `section.config(tab, dropdownValues, selectedLevel, mainTab, selectedTopTab)`. */
export type SectionBuild = (context: SectionBuildContext) => SectionDataVisibleConfig

export interface ReportSectionConfig {
  /** DOM anchor for `#hash` deep links AND the customizeStore persistence partition. */
  id: string
  /**
   * `data` renders through DataVisible. `pending` is a fully-ported config whose v3 renderer
   * (dashboard chart area / n-gram / bespoke component) has not landed in v4 yet — the section
   * is skipped at render time and `pendingNote` names what it waits for.
   */
  kind?: 'data' | 'pending'
  pendingNote?: string
  titleKey?: string
  descriptionKey?: string
  /**
   * A registrant-owned section renderer (v3 `section.component`, `OrphexReportPage.vue:64`). When
   * set, `ReportPage` mounts it under the section anchor INSTEAD of the config-driven
   * `ReportSection`, and every other field on this section is ignored.
   *
   * This is the field that makes `registerReportLayout` load-bearing rather than cosmetic: the
   * component reference travels with the registration, so 08 renders a vertical's chart without
   * importing the vertical. It receives no props — exactly v3's contract — because a component
   * reaching 08 through the registry already owns its own data path (08's barrel: `DataVisible`,
   * `useReportData`).
   */
  component?: Component
  /**
   * A registrant-owned renderer for the section's data body. Unlike `component`, this keeps the
   * standard section chrome (tabs, dropdowns, group/level controls) and receives the resolved
   * build config plus the active date fragment. `prepend` renders the custom surface above the
   * regular DataVisible body; `replace` makes it the body.
   */
  renderer?: Component
  rendererMode?: 'replace' | 'prepend'
  rendererProps?: Record<string, unknown>
  /** Availability + section-title context report; overrides inner-tab `reportId` in gating. */
  reportId?: number
  tabs: ReportSectionTabConfig[]
  build?: SectionBuild
  /** Render `tabs` as a centered segmented control instead of the left channel rail (v3 `topTabField`). */
  topTabMode?: boolean
  /** A second, independent top-level segmented control (v3 `topTabs`, SearchAdsExcellence only). */
  topTabs?: SectionTopTab[]
  hideChannels?: boolean
  /** Render every tab eagerly and auto-hide the ones whose request reports empty (v3 `checkShow`). */
  checkShow?: boolean
  /** Section-local date picker instead of the page-level one. */
  showDatePicker?: boolean
  /** Default picker preset (v3 `defaultRange`/`selectedDate` literals are valid ids). */
  defaultPreset?: DatePresetId
  hiddenCompare?: boolean
  compareDefault?: boolean
  /** Shows the Ad/Creative Level select (CreativeDashboard). */
  creativeLevel?: boolean
  defaultLevel?: SectionLevelId
  /** Section-level entity group select (BudgetAllocation applies it per-tab too). */
  entityType?: string
}

export interface ReportPageTabConfig {
  /** Machine identity — selection + persistence key. */
  name: string
  labelKey: string
  sections: ReportSectionConfig[]
  /** Page date-picker compare toggle default (v3 `?? true`). */
  compareDefault?: boolean
  /** Restrict the page picker to these preset ids. */
  selectablePresets?: DatePresetId[]
  /** Default page picker preset (v3 per-section `defaultDateValue`, hoisted). */
  defaultPreset?: DatePresetId
  hideDatePicker?: boolean
  visibleRule?: LayoutVisibleRule
  /** Drop this tab when the workspace lacks `ga4_key_event_mapping` (v3 'Key Events' name gate). */
  requiresKeyEventMapping?: boolean
}

/** One report page's layout: the config a route's `layoutKey` resolves to. */
export type ReportLayoutConfig = ReportPageTabConfig[]

/** Re-exported so config modules can type initial views without reaching into chart types. */
export type SectionViewType = ReportViewType
